import { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronsDown, ChevronsUp } from 'lucide-react';
import { useEditorStore } from '../../stores/editor-store';

interface LayersPanelProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function LayersPanel({ collapsed = false, onToggle }: LayersPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editor = useEditorStore((s) => s.editorInstance);
  const [isReady, setIsReady] = useState(false);
  const [treeExpanded, setTreeExpanded] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!editor || !containerRef.current) return;

    const onLoad = () => {
      setIsReady(true);
    };

    if (editor.getComponents().length > 0) {
      setIsReady(true);
    } else {
      editor.on('load', onLoad);
    }

    return () => {
      editor.off('load', onLoad);
    };
  }, [editor]);

  const renderLayers = useCallback(() => {
    if (!editor || !containerRef.current) return;
    const layerManager = editor.Layers;
    const container = containerRef.current;

    // Re-render to avoid stale/empty tree after bulk setComponents calls.
    container.innerHTML = '';
    try {
      const layersEl = layerManager.render();
      if (layersEl) {
        container.appendChild(layersEl);
      }
    } catch (e) {
      console.warn('Failed to render layers:', e);
    }
  }, [editor]);

  const setAllLayersOpen = useCallback(
    (open: boolean) => {
      if (!editor) return;
      const wrapper = editor.getWrapper?.();
      if (!wrapper) return;

      const visit = (cmp: any) => {
        const children = cmp?.components?.();
        const hasChildren = Boolean(children && typeof children.length === 'number' && children.length > 0);
        if (hasChildren) {
          // `open` controls the expanded/collapsed state in the Layers UI.
          try {
            cmp.set?.('open', open);
          } catch {}
          children.forEach?.((child: any) => visit(child));
        }
      };

      visit(wrapper);
      setTreeExpanded(open);
      renderLayers();
    },
    [editor, renderLayers]
  );

  useEffect(() => {
    if (!editor || !containerRef.current || !isReady || collapsed) return;

    // Initial render
    renderLayers();

    // Debounced update — GrapesJS Layers manager updates its own DOM,
    // we just need to trigger a re-render after bulk operations settle
    const debouncedUpdate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        renderLayers();
      }, 300);
    };

    editor.on('component:add', debouncedUpdate);
    editor.on('component:remove', debouncedUpdate);
    editor.on('component:update', debouncedUpdate);
    editor.on('load', debouncedUpdate);

    return () => {
      editor.off('component:add', debouncedUpdate);
      editor.off('component:remove', debouncedUpdate);
      editor.off('component:update', debouncedUpdate);
      editor.off('load', debouncedUpdate);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [editor, isReady, collapsed, renderLayers]);

  return (
    <div className="flex flex-col h-full bg-surface border-r border-surface-lighter">
      <div
        className={`py-3 border-b border-surface-lighter flex items-center ${collapsed ? 'px-2 justify-center' : 'px-3 justify-between'
          }`}
      >
        {!collapsed && (
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-text">Layers</h3>
            <button
              type="button"
              onClick={() => setAllLayersOpen(!treeExpanded)}
              className="text-text-muted hover:text-text transition-colors"
              title={treeExpanded ? 'Collapse all layers' : 'Expand all layers'}
            >
              {treeExpanded ? <ChevronsUp size={16} /> : <ChevronsDown size={16} />}
            </button>
          </div>
        )}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="text-text-muted hover:text-text transition-colors"
            title={collapsed ? 'Expand layers panel' : 'Collapse layers panel'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>
      {!collapsed && (
        <div
          ref={containerRef}
          className="flex-1 overflow-auto layers-container"
        />
      )}
    </div>
  );
}
